import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';
import { IconCheck } from '@tabler/icons-react';

export const VisuallyHiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`;

const CheckboxContainer = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  cursor: pointer;
  user-select: none;
`;

interface CheckboxBoxProps {
  checked: boolean;
  disabled?: boolean;
}

const CheckboxBox = styled('span', {
  shouldForwardProp: prop => isPropValid(prop) && !['checked', 'disabled'].includes(prop)
})<CheckboxBoxProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.xs};
  background: ${({ checked, theme }) =>
    checked ? theme.accent.primary : theme.background.secondary};
  transition: ${({ theme }) => theme.animation.transition.standard};
  flex-shrink: 0;

  ${({ disabled }) => disabled && `opacity: 0.5; cursor: not-allowed;`}

  &:hover {
    border-color: ${({ theme }) => theme.accent.primary};
  }

  input:focus-visible + & {
    outline: none;
    box-shadow: ${({ theme }) => theme.focusShadow(theme.accent.tertiary)};
  }

  svg {
    color: white;
    opacity: ${({ checked }) => checked ? 1 : 0};
    transition: opacity 0.15s ease;
  }
`;

const CheckboxLabel = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.primary};
`;

interface CheckboxProps {
  checked: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  label?: string;
  id?: string;
}

export function Checkbox({
  checked,
  onChange,
  disabled,
  label,
  id,
  ...props
}: CheckboxProps) {
  return (
    <CheckboxContainer>
      <VisuallyHiddenInput
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        id={id}
        {...props}
      />
      <CheckboxBox checked={checked} disabled={disabled}>
        <IconCheck size={12} stroke={2.5} />
      </CheckboxBox>
      {label && <CheckboxLabel>{label}</CheckboxLabel>}
    </CheckboxContainer>
  );
}
